import { SSEEventTypesEnum } from "../../detail-ost/enums/sse-event-types.enum";
import { OstResponse } from "../ost-response.model";

export interface OstEvent {
  ostNum: number;
  eventType: SSEEventTypesEnum | string;
  f11Num?: number | null;
  f11EstadoCreacion?: boolean | null;
  ostSubestado?: string | null;
  ostEstado?: string | null;
  ostEstadoGestionProducto?: string | null;
  f11Estado?: string | null;
  nGuiaDespacho?: string | null;
  ostIsF11Empresa?: boolean | null;
  etiquetaCreada?: boolean | null;
}

export type OstEventIdentifierField = "ostNum" | "eventType";
export type OstEventUpdatableField = Exclude<keyof OstEvent, OstEventIdentifierField>;

export const OST_EVENT_UPDATABLE_FIELDS = [
  "f11Num",
  "f11EstadoCreacion",
  "ostSubestado",
  "ostEstado",
  "ostEstadoGestionProducto",
  "f11Estado",
  "nGuiaDespacho",
  "ostIsF11Empresa",
  "etiquetaCreada",
] as const satisfies readonly OstEventUpdatableField[];

export const OST_EVENT_SSE_EVENT_TYPES = [
  SSEEventTypesEnum.F11_NUM,
  SSEEventTypesEnum.GUIA_DESPACHO,
  SSEEventTypesEnum.OST_STATE,
  SSEEventTypesEnum.OST_UPDATED_SUB_STATE,
  SSEEventTypesEnum.PRODUCT_STATE,
  SSEEventTypesEnum.F11_STATE,
  SSEEventTypesEnum.OST_IS_F11_EMPRESA,
  SSEEventTypesEnum.LABEL_CREATED,
] as const satisfies readonly SSEEventTypesEnum[];

export type OstEventSseEventType = (typeof OST_EVENT_SSE_EVENT_TYPES)[number];

const OST_EVENT_TO_OST_RESPONSE_FIELD = {
  f11Num: "f11Num",
  f11EstadoCreacion: "f11EstadoCreacion",
  ostSubestado: "ostSubestado",
  ostEstado: "ostEstado",
  ostEstadoGestionProducto: "ostEstadoGestionProducto",
  f11Estado: "f11Estado",
  nGuiaDespacho: "f11NumeroGuiaDespacho",
  ostIsF11Empresa: "ostIsF11Empresa",
  etiquetaCreada: "etiquetaCreada",
} as const satisfies Record<OstEventUpdatableField, keyof OstResponse>;

interface OstEventStreamPayload {
  payload: string;
  eventType?: string;
}

export function isOstEventSseEventType(eventType: string): eventType is OstEventSseEventType {
  return OST_EVENT_SSE_EVENT_TYPES.some((ostEventType: OstEventSseEventType): boolean => String(ostEventType) === eventType);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeOstNum(value: unknown): number | undefined {
  const ostNum = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  return Number.isInteger(ostNum) && ostNum > 0 ? ostNum : undefined;
}

function normalizeEventType(eventType: unknown, fallbackEventType?: string): OstEventSseEventType | undefined {
  if (typeof eventType === "string" && isOstEventSseEventType(eventType)) {
    return eventType;
  }

  if (fallbackEventType && isOstEventSseEventType(fallbackEventType)) {
    return fallbackEventType;
  }

  return undefined;
}

function isValidOstEventFieldValue(field: OstEventUpdatableField, value: unknown): boolean {
  if (value === undefined || value === null) {
    return value === null;
  }

  switch (field) {
    case "f11Num":
      return typeof value === "number";
    case "f11EstadoCreacion":
    case "ostIsF11Empresa":
    case "etiquetaCreada":
      return typeof value === "boolean";
    case "ostSubestado":
    case "ostEstado":
    case "ostEstadoGestionProducto":
    case "f11Estado":
    case "nGuiaDespacho":
      return typeof value === "string";
  }
}

function pickOstEventFields(parsedEvent: Record<string, unknown>): Partial<Omit<OstEvent, OstEventIdentifierField>> {
  const entries = OST_EVENT_UPDATABLE_FIELDS.filter((field: OstEventUpdatableField): boolean => isValidOstEventFieldValue(field, parsedEvent[field])).map(
    (field: OstEventUpdatableField): [OstEventUpdatableField, OstEvent[OstEventUpdatableField]] => [field, parsedEvent[field] as OstEvent[OstEventUpdatableField]]
  );

  return Object.fromEntries(entries) as Partial<Omit<OstEvent, OstEventIdentifierField>>;
}

function normalizeOstEvent(parsedEvent: unknown, fallbackEventType?: string): OstEvent | undefined {
  if (!isRecord(parsedEvent)) {
    return undefined;
  }

  const ostNum = normalizeOstNum(parsedEvent["ostNum"]);
  const eventType = normalizeEventType(parsedEvent["eventType"], fallbackEventType);

  if (ostNum === undefined || eventType === undefined) {
    return undefined;
  }

  return {
    ostNum,
    eventType,
    ...pickOstEventFields(parsedEvent),
  };
}

function extractEventTypeFromPrefix(prefix: string): string | undefined {
  return prefix.split(/\s+/).find((part: string): boolean => isOstEventSseEventType(part));
}

function extractSseDataPayload(data: string): OstEventStreamPayload | undefined {
  const lines = data.split(/\r?\n/).map((line: string): string => line.trim());
  const dataLines = lines.filter((line: string): boolean => line.startsWith("data:")).map((line: string): string => line.slice("data:".length).trimStart());

  if (dataLines.length === 0) {
    return undefined;
  }

  const eventLine = lines.find((line: string): boolean => line.startsWith("event:"));
  const eventType = eventLine?.slice("event:".length).trim();

  return {
    payload: dataLines.join("\n"),
    eventType: eventType && isOstEventSseEventType(eventType) ? eventType : undefined,
  };
}

function extractOstEventPayload(data: string): OstEventStreamPayload | undefined {
  const trimmedData = data.trim();

  if (!trimmedData) {
    return undefined;
  }

  const ssePayload = extractSseDataPayload(trimmedData);

  if (ssePayload) {
    return ssePayload;
  }

  const jsonStartIndex = trimmedData.indexOf("{");

  if (jsonStartIndex === -1) {
    return { payload: trimmedData };
  }

  const jsonEndIndex = trimmedData.lastIndexOf("}");
  const payloadEndIndex = jsonEndIndex >= jsonStartIndex ? jsonEndIndex + 1 : trimmedData.length;
  const prefix = trimmedData.slice(0, jsonStartIndex).trim();

  return {
    payload: trimmedData.slice(jsonStartIndex, payloadEndIndex),
    eventType: extractEventTypeFromPrefix(prefix),
  };
}

export function parseOstEventStreamPayload(data: string, fallbackEventType?: string): OstEvent | undefined {
  const eventPayload = extractOstEventPayload(data);

  if (!eventPayload) {
    return undefined;
  }

  try {
    const parsedEvent = JSON.parse(eventPayload.payload) as unknown;
    return normalizeOstEvent(parsedEvent, eventPayload.eventType ?? fallbackEventType);
  } catch {
    return undefined;
  }
}

export function getOstResponseChangesFromEvent(event: OstEvent): Partial<OstResponse> {
  let normalizedChanges = OST_EVENT_UPDATABLE_FIELDS.reduce<Partial<OstResponse>>(
    (currentChanges: Partial<OstResponse>, eventField: OstEventUpdatableField): Partial<OstResponse> => {
      const value = event[eventField];

      if (value === undefined || value === null) {
        return currentChanges;
      }

      const responseField = OST_EVENT_TO_OST_RESPONSE_FIELD[eventField];
      return { ...currentChanges, [responseField]: value };
    },
    {}
  );

  if (event.f11Num !== undefined && event.f11Num !== null && normalizedChanges.f11EstadoCreacion === undefined) {
    normalizedChanges = { ...normalizedChanges, f11EstadoCreacion: true };
  }

  if (String(event.eventType) === String(SSEEventTypesEnum.LABEL_CREATED) && normalizedChanges.etiquetaCreada === undefined) {
    normalizedChanges = { ...normalizedChanges, etiquetaCreada: true };
  }

  return normalizedChanges;
}

export class OstSseEventModel implements OstEvent {
  ostNum: number;
  f11Num: number;
  eventType: string;
  f11EstadoCreacion?: boolean;
  ostSubestado: string;
  ostEstado: string;
  ostEstadoGestionProducto: string;
  f11Estado: string;
  nGuiaDespacho: string;
  ostIsF11Empresa: boolean;
  etiquetaCreada: boolean;

  constructor(
    ostNum: number,
    f11Num: number,
    eventType: string,
    ostSubestado: string,
    ostEstado: string,
    ostEstadoGestionProducto: string,
    f11Estado: string,
    nGuiaDespacho: string,
    ostIsF11Empresa: boolean,
    etiquetaCreada: boolean,
    f11EstadoCreacion?: boolean
  ) {
    this.ostNum = ostNum;
    this.f11Num = f11Num;
    this.eventType = eventType;
    this.f11EstadoCreacion = f11EstadoCreacion;
    this.ostSubestado = ostSubestado;
    this.ostEstado = ostEstado;
    this.ostEstadoGestionProducto = ostEstadoGestionProducto;
    this.f11Estado = f11Estado;
    this.nGuiaDespacho = nGuiaDespacho;
    this.ostIsF11Empresa = ostIsF11Empresa;
    this.etiquetaCreada = etiquetaCreada;
  }
}
